import { Category } from '@Types/category.types';

export interface DropTarget {
	categoryName: string;
	componentName: string;
}

/** Distribute integer weights that sum to exactly 100 using largest remainder. */
export function distributeWeightsTo100(weights: number[]): number[] {
	if (weights.length === 0) {
		return [];
	}
	const total = weights.reduce((sum, weight) => sum + weight, 0);
	if (total <= 0) {
		return weights.map(() => 0);
	}

	const scaled = weights.map(weight => (weight / total) * 100);
	const floors = scaled.map(value => Math.floor(value));
	const remainder = 100 - floors.reduce((sum, value) => sum + value, 0);
	const byRemainder = scaled
		.map((value, index) => ({ index, remainder: value - floors[index] }))
		.sort((a, b) => b.remainder - a.remainder);

	const result = [...floors];
	for (let i = 0; i < remainder; i++) {
		result[byRemainder[i].index] += 1;
	}
	return result;
}

export function dropUnavailableComponents(
	categories: Category[],
	toDrop: DropTarget[],
): Category[] {
	const dropSet = new Set(
		toDrop.map(d => `${d.categoryName}\0${d.componentName}`),
	);
	return categories.map(cat => {
		const components = cat.components ?? [];
		const remaining = components.filter(
			c => !dropSet.has(`${cat.name}\0${c.name}`),
		);
		if (remaining.length === components.length) {
			// Nothing was dropped from this category — leave its weights
			// as the user set them, even if they don't sum to 100.
			return cat;
		}
		const selected = remaining.filter(c => c.select);
		const total = selected.reduce((s, c) => s + c.weighting, 0);
		if (total <= 0 || selected.length === 0) {
			return { ...cat, components: remaining };
		}
		const scale = 100 / total;
		const selectedWeights = selected.map(c => c.weighting * scale);
		const distributed = distributeWeightsTo100(selectedWeights);
		let selectedIndex = 0;
		return {
			...cat,
			components: remaining.map(c => {
				if (!c.select) {
					return c;
				}
				const weighting = distributed[selectedIndex];
				selectedIndex += 1;
				return { ...c, weighting };
			}),
		};
	});
}
